import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Separator } from '../components/ui/separator';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import { User, Bell, Shield, CreditCard, Palette, Globe, Database, HelpCircle, Download, Users, Plus, Edit, Trash2, Mail, Phone, Lock, CheckCircle } from 'lucide-react';
import { Dialog } from '@headlessui/react';
import Layout from '../components/Layout';
import { usersService } from '../services/api.service';
import { PERMISSIONS, PERMISSION_LABELS, PERMISSION_CATEGORIES } from '../config/permissionsConfig';
import logger from '../utils/logger';

const SettingsPage = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('notifications');
  const [notifications, setNotifications] = useState({
    emailNotifications: true,
    pushNotifications: false,
    smsNotifications: true,
    caseUpdates: true,
    newClientAlerts: true,
    paymentReminders: true,
    appointmentReminders: true,
    systemUpdates: false
  });
  
  // بيانات المستخدمين
  const [users, setUsers] = useState([]);
  const [showAddUserForm, setShowAddUserForm] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [newUser, setNewUser] = useState({
    name: '',
    email: '',
    phone: '',
    role: 'employee',
    password: '',
    confirmPassword: ''
  });

  // إدارة الصلاحيات
  const [showPermissionsDialog, setShowPermissionsDialog] = useState(false);
  const [selectedUserForPermissions, setSelectedUserForPermissions] = useState(null);
  const [selectedPermissions, setSelectedPermissions] = useState([]);
  const [loadingPermissions, setLoadingPermissions] = useState(false);

  const handleNotificationChange = (key, value) => {
    setNotifications(prev => ({
      ...prev,
      [key]: value
    }));
  };

  // جلب المستخدمين
  const loadUsers = async () => {
    try {
      const data = await usersService.getUsers();
      setUsers(data);
    } catch (error) {
      logger.error('Error loading users:', error);
      alert(error.message || 'فشل في تحميل المستخدمين');
    }
  };

  // عند تحميل التبويب
  useEffect(() => {
    if (activeTab === 'users') {
      loadUsers();
    }
  }, [activeTab]);

  // إضافة مستخدم جديد
  const handleAddUser = async () => {
    try {
      if (!newUser.name || !newUser.email || !newUser.password) {
        alert('الرجاء تعبئة جميع الحقول المطلوبة');
        return;
      }

      if (newUser.password !== newUser.confirmPassword) {
        alert('كلمات المرور غير متطابقة');
        return;
      }

      await usersService.createUser({
        name: newUser.name,
        email: newUser.email,
        password: newUser.password,
        role: newUser.role,
        phone: newUser.phone || null,
      });

      alert('تم إضافة المستخدم بنجاح');
      setShowAddUserForm(false);
      setNewUser({
        name: '',
        email: '',
        phone: '',
        role: 'employee',
        password: '',
        confirmPassword: ''
      });
      loadUsers();
    } catch (error) {
      logger.error('Error adding user:', error);
      alert(error.message || 'فشل في إضافة المستخدم');
    }
  };

  // تحديث حالة المستخدم
  const handleToggleUserStatus = async (userId, currentStatus) => {
    try {
      const newStatus = currentStatus === 'active' ? 'inactive' : 'active';
      await usersService.updateUserStatus(userId, newStatus);
      alert('تم تحديث حالة المستخدم');
      loadUsers();
    } catch (error) {
      logger.error('Error updating user status:', error);
      alert(error.message || 'فشل في تحديث حالة المستخدم');
    }
  };

  // فتح نافذة إدارة الصلاحيات
  const handleOpenPermissions = async (user) => {
    setSelectedUserForPermissions(user);
    setShowPermissionsDialog(true);
    setLoadingPermissions(true);
    
    try {
      const data = await usersService.getUserPermissions(user.id);
      setSelectedPermissions(data.permissions || []);
    } catch (error) {
      logger.error('Error loading permissions:', error);
      alert(error.message || 'فشل في تحميل الصلاحيات');
    } finally {
      setLoadingPermissions(false);
    }
  };

  // تحديث صلاحية واحدة (تفعيل/تعطيل)
  const handleTogglePermission = (permission) => {
    setSelectedPermissions(prev => {
      if (prev.includes(permission)) {
        return prev.filter(p => p !== permission);
      } else {
        return [...prev, permission];
      }
    });
  };

  // حفظ الصلاحيات
  const handleSavePermissions = async () => {
    try {
      await usersService.updateUserPermissions(selectedUserForPermissions.id, selectedPermissions);
      alert('تم تحديث الصلاحيات بنجاح');
      setShowPermissionsDialog(false);
      loadUsers();
    } catch (error) {
      logger.error('Error saving permissions:', error);
      alert(error.message || 'فشل في حفظ الصلاحيات');
    }
  };

  // إعادة تعيين الصلاحيات إلى الافتراضية
  const handleResetPermissions = async () => {
    if (!window.confirm('هل أنت متأكد من إعادة تعيين الصلاحيات إلى الافتراضية؟')) {
      return;
    }

    try {
      const data = await usersService.resetUserPermissions(selectedUserForPermissions.id);
      setSelectedPermissions(data.permissions || []);
      alert('تم إعادة تعيين الصلاحيات بنجاح');
    } catch (error) {
      logger.error('Error resetting permissions:', error);
      alert(error.message || 'فشل في إعادة تعيين الصلاحيات');
    }
  };

  return (
    <Layout>
      <div className="container mx-auto p-4 md:p-6" dir="rtl">
      <h1 className="text-2xl font-bold text-gray-800 mb-6">الإعدادات</h1>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="grid w-full grid-cols-5">
          <TabsTrigger value="notifications" className="flex items-center gap-2">
            <Bell className="h-4 w-4" />
            <span className="hidden sm:inline">الإشعارات</span>
          </TabsTrigger>
          <TabsTrigger value="security" className="flex items-center gap-2">
            <Shield className="h-4 w-4" />
            <span className="hidden sm:inline">الأمان</span>
          </TabsTrigger>
          <TabsTrigger value="billing" className="flex items-center gap-2">
            <CreditCard className="h-4 w-4" />
            <span className="hidden sm:inline">الفواتير</span>
          </TabsTrigger>
          <TabsTrigger value="advanced" className="flex items-center gap-2">
            <Database className="h-4 w-4" />
            <span className="hidden sm:inline">متقدم</span>
          </TabsTrigger>
          <TabsTrigger value="users" className="flex items-center gap-2">
            <Users className="h-4 w-4" />
            <span className="hidden sm:inline">المستخدمون</span>
          </TabsTrigger>
        </TabsList>



        <TabsContent value="notifications" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>إعدادات الإشعارات</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-4">
                <h3 className="text-lg font-medium">قنوات الإشعارات</h3>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <Label htmlFor="emailNotifications">الإشعارات عبر البريد الإلكتروني</Label>
                      <p className="text-sm text-gray-500">تلقي إشعارات عبر البريد الإلكتروني</p>
                    </div>
                    <Switch 
                      id="emailNotifications"
                      checked={notifications.emailNotifications}
                      onCheckedChange={(checked) => handleNotificationChange('emailNotifications', checked)}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <Label htmlFor="pushNotifications">الإشعارات الفورية</Label>
                      <p className="text-sm text-gray-500">تلقي إشعارات فورية في المتصفح</p>
                    </div>
                    <Switch 
                      id="pushNotifications"
                      checked={notifications.pushNotifications}
                      onCheckedChange={(checked) => handleNotificationChange('pushNotifications', checked)}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <Label htmlFor="smsNotifications">الإشعارات عبر الرسائل النصية</Label>
                      <p className="text-sm text-gray-500">تلقي إشعارات عبر الرسائل النصية القصيرة</p>
                    </div>
                    <Switch 
                      id="smsNotifications"
                      checked={notifications.smsNotifications}
                      onCheckedChange={(checked) => handleNotificationChange('smsNotifications', checked)}
                    />
                  </div>
                </div>
              </div>

              <Separator />

              <div className="space-y-4">
                <h3 className="text-lg font-medium">أنواع الإشعارات</h3>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <Label htmlFor="caseUpdates">تحديثات القضايا</Label>
                      <p className="text-sm text-gray-500">إشعارات عند تحديث حالة القضايا</p>
                    </div>
                    <Switch 
                      id="caseUpdates"
                      checked={notifications.caseUpdates}
                      onCheckedChange={(checked) => handleNotificationChange('caseUpdates', checked)}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <Label htmlFor="newClientAlerts">تنبيهات العملاء الجدد</Label>
                      <p className="text-sm text-gray-500">إشعارات عند إضافة عملاء جدد</p>
                    </div>
                    <Switch 
                      id="newClientAlerts"
                      checked={notifications.newClientAlerts}
                      onCheckedChange={(checked) => handleNotificationChange('newClientAlerts', checked)}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <Label htmlFor="paymentReminders">تذكيرات الدفع</Label>
                      <p className="text-sm text-gray-500">تذكيرات بالمدفوعات المستحقة</p>
                    </div>
                    <Switch 
                      id="paymentReminders"
                      checked={notifications.paymentReminders}
                      onCheckedChange={(checked) => handleNotificationChange('paymentReminders', checked)}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <Label htmlFor="appointmentReminders">تذكيرات المواعيد</Label>
                      <p className="text-sm text-gray-500">تذكيرات بالمواعيد القادمة</p>
                    </div>
                    <Switch 
                      id="appointmentReminders"
                      checked={notifications.appointmentReminders}
                      onCheckedChange={(checked) => handleNotificationChange('appointmentReminders', checked)}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <Label htmlFor="systemUpdates">تحديثات النظام</Label>
                      <p className="text-sm text-gray-500">إشعارات بتحديثات النظام والميزات الجديدة</p>
                    </div>
                    <Switch 
                      id="systemUpdates"
                      checked={notifications.systemUpdates}
                      onCheckedChange={(checked) => handleNotificationChange('systemUpdates', checked)}
                    />
                  </div>
                </div>
              </div>

              <div className="flex justify-end pt-4">
                <Button>حفظ التغييرات</Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="security" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>الأمان والخصوصية</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-4">
                <h3 className="text-lg font-medium">تغيير كلمة المرور</h3>
                <div className="space-y-3">
                  <div className="space-y-2">
                    <Label htmlFor="currentPassword">كلمة المرور الحالية</Label>
                    <Input id="currentPassword" type="password" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="newPassword">كلمة المرور الجديدة</Label>
                    <Input id="newPassword" type="password" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="confirmPassword">تأكيد كلمة المرور الجديدة</Label>
                    <Input id="confirmPassword" type="password" />
                  </div>
                  <Button>تغيير كلمة المرور</Button>
                </div>
              </div>

              <Separator />

              <div className="space-y-4">
                <h3 className="text-lg font-medium">المصادقة الثنائية</h3>
                <div className="space-y-3">
                  <p className="text-sm text-gray-600">تفعيل المصادقة الثنائية يضيف طبقة إضافية من الأمان لحسابك</p>
                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <Label htmlFor="twoFactorAuth">المصادقة الثنائية</Label>
                      <p className="text-sm text-gray-500">استخدام تطبيق المصادقة للتحقق من هويتك</p>
                    </div>
                    <Switch id="twoFactorAuth" />
                  </div>
                </div>
              </div>

              <Separator />

              <div className="space-y-4">
                <h3 className="text-lg font-medium">الجلسات النشطة</h3>
                <div className="space-y-3">
                  <div className="border rounded-lg p-4">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-medium">المتصفح الحالي</p>
                        <p className="text-sm text-gray-500">Chrome على Windows</p>
                        <p className="text-xs text-gray-400">آخر نشاط: الآن</p>
                      </div>
                      <Badge variant="outline">نشط</Badge>
                    </div>
                  </div>
                  <div className="border rounded-lg p-4">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-medium">موبايل</p>
                        <p className="text-sm text-gray-500">Safari على iPhone</p>
                        <p className="text-xs text-gray-400">آخر نشاط: منذ 3 ساعات</p>
                      </div>
                      <Button variant="outline" size="sm">تسجيل الخروج</Button>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="billing" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>معلومات الفواتير والاشتراك</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-4">
                <h3 className="text-lg font-medium">الخطة الحالية</h3>
                <div className="border rounded-lg p-4">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="font-medium text-lg">خطة احترافية</p>
                      <p className="text-sm text-gray-500">299 ريال / شهر</p>
                      <p className="text-xs text-gray-400 mt-2">التجديد التالي: 15 نوفمبر 2023</p>
                    </div>
                    <Button variant="outline">ترقية الخطة</Button>
                  </div>
                </div>
              </div>

              <Separator />

              <div className="space-y-4">
                <h3 className="text-lg font-medium">طرق الدفع</h3>
                <div className="space-y-3">
                  <div className="border rounded-lg p-4">
                    <div className="flex justify-between items-start">
                      <div className="flex items-center gap-3">
                        <div className="bg-blue-100 p-2 rounded-lg">
                          <CreditCard className="h-5 w-5 text-blue-600" />
                        </div>
                        <div>
                          <p className="font-medium">بطاقة الائتمان</p>
                          <p className="text-sm text-gray-500">**** **** **** 4242</p>
                          <p className="text-xs text-gray-400">تنتهي في 12/2025</p>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button variant="outline" size="sm">تعديل</Button>
                        <Button variant="outline" size="sm">حذف</Button>
                      </div>
                    </div>
                  </div>
                  <Button variant="outline" className="w-full">إضافة طريقة دفع جديدة</Button>
                </div>
              </div>

              <Separator />

              <div className="space-y-4">
                <h3 className="text-lg font-medium">سجل الفواتير</h3>
                <div className="space-y-3">
                  <div className="border rounded-lg p-4">
                    <div className="flex justify-between items-center">
                      <div>
                        <p className="font-medium">فاتورة أكتوبر 2023</p>
                        <p className="text-sm text-gray-500">صدرت في 15 أكتوبر 2023</p>
                      </div>
                      <div className="flex gap-2 items-center">
                        <p className="font-medium">299 ريال</p>
                        <Button variant="outline" size="sm">
                          <Download className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                  <div className="border rounded-lg p-4">
                    <div className="flex justify-between items-center">
                      <div>
                        <p className="font-medium">فاتورة سبتمبر 2023</p>
                        <p className="text-sm text-gray-500">صدرت في 15 سبتمبر 2023</p>
                      </div>
                      <div className="flex gap-2 items-center">
                        <p className="font-medium">299 ريال</p>
                        <Button variant="outline" size="sm">
                          <Download className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>



        <TabsContent value="advanced" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>الإعدادات المتقدمة</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-4">
                <h3 className="text-lg font-medium">إدارة البيانات</h3>
                <div className="space-y-3">
                  <div className="border rounded-lg p-4">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-medium">نسخ احتياطي للبيانات</p>
                        <p className="text-sm text-gray-500">إنشاء نسخة احتياطية من جميع بياناتك</p>
                      </div>
                      <Button variant="outline">إنشاء نسخة احتياطية</Button>
                    </div>
                  </div>
                  <div className="border rounded-lg p-4">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-medium">استيراد البيانات</p>
                        <p className="text-sm text-gray-500">استيراد البيانات من ملف</p>
                      </div>
                      <Button variant="outline">استيراد</Button>
                    </div>
                  </div>
                  <div className="border rounded-lg p-4">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-medium">تصدير البيانات</p>
                        <p className="text-sm text-gray-500">تصدير جميع بياناتك كملف</p>
                      </div>
                      <Button variant="outline">تصدير</Button>
                    </div>
                  </div>
                </div>
              </div>

              <Separator />

              <div className="space-y-4">
                <h3 className="text-lg font-medium">التكامل مع الأنظمة الخارجية</h3>
                <div className="space-y-3">
                  <div className="border rounded-lg p-4">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-medium">التقويم</p>
                        <p className="text-sm text-gray-500">المزامنة مع Google Calendar أو Outlook</p>
                      </div>
                      <Button variant="outline">إعداد</Button>
                    </div>
                  </div>
                  <div className="border rounded-lg p-4">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-medium">التخزين السحابي</p>
                        <p className="text-sm text-gray-500">الاتصال بـ Google Drive أو Dropbox</p>
                      </div>
                      <Button variant="outline">إعداد</Button>
                    </div>
                  </div>
                  <div className="border rounded-lg p-4">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-medium">البريد الإلكتروني</p>
                        <p className="text-sm text-gray-500">إعداد حساب البريد الإلكتروني المخصص</p>
                      </div>
                      <Button variant="outline">إعداد</Button>
                    </div>
                  </div>
                </div>
              </div>

              <Separator />

              <div className="space-y-4">
                <h3 className="text-lg font-medium text-red-600">خيارات خطرة</h3>
                <div className="space-y-3">
                  <div className="border border-red-200 rounded-lg p-4 bg-red-50">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-medium text-red-600">حذف الحساب</p>
                        <p className="text-sm text-gray-500">سيتم حذف جميع بياناتك بشكل نهائي</p>
                      </div>
                      <Button variant="destructive">حذف الحساب</Button>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="users" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <CardTitle>إدارة المستخدمين</CardTitle>
                <Button onClick={() => setShowAddUserForm(true)} className="flex items-center gap-2">
                  <Plus className="h-4 w-4" />
                  إضافة مستخدم
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {users.length === 0 ? (
                  <p className="text-center text-gray-500 py-8">لا يوجد مستخدمون بعد</p>
                ) : (
                  users.map((user) => (
                    <div key={user.id} className="border rounded-lg p-4">
                      <div className="flex justify-between items-start">
                        <div className="flex items-center gap-3">
                          <Avatar className="h-10 w-10">
                            <AvatarFallback>{user.name?.charAt(0) || 'U'}</AvatarFallback>
                          </Avatar>
                          <div>
                            <p className="font-medium">{user.name}</p>
                            <div className="flex items-center gap-2 text-sm text-gray-500">
                              <Mail className="h-3 w-3" />
                              {user.email}
                            </div>
                            {user.phone && (
                              <div className="flex items-center gap-2 text-sm text-gray-500">
                                <Phone className="h-3 w-3" />
                                {user.phone}
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge variant={user.role === 'admin' ? 'default' : 'secondary'}>
                            {user.role === 'admin' ? 'مدير' : user.role === 'lawyer' ? 'محامي' : 'موظف'}
                          </Badge>
                          <Badge variant={user.status === 'active' ? 'default' : 'secondary'}>
                            {user.status === 'active' ? 'نشط' : 'غير نشط'}
                          </Badge>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleToggleUserStatus(user.id, user.status)}
                          >
                            {user.status === 'active' ? 'تعطيل' : 'تفعيل'}
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleOpenPermissions(user)}
                            className="flex items-center gap-1"
                          >
                            <Shield className="h-3 w-3" />
                            الصلاحيات
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Add User Dialog */}
      <Dialog open={showAddUserForm} onClose={() => setShowAddUserForm(false)} className="relative z-50">
        <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
        
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <Dialog.Panel className="mx-auto max-w-md w-full rounded-lg bg-white p-6">
            <Dialog.Title className="text-lg font-medium text-gray-900 mb-4">
              إضافة مستخدم جديد
            </Dialog.Title>
            
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="newUserName">الاسم *</Label>
                <Input
                  id="newUserName"
                  value={newUser.name}
                  onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                  placeholder="أدخل الاسم الكامل"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="newUserEmail">البريد الإلكتروني *</Label>
                <Input
                  id="newUserEmail"
                  type="email"
                  value={newUser.email}
                  onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                  placeholder="email@example.com"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="newUserPhone">رقم الهاتف</Label>
                <Input
                  id="newUserPhone"
                  value={newUser.phone}
                  onChange={(e) => setNewUser({ ...newUser, phone: e.target.value })}
                  placeholder="اختياري"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="newUserRole">الدور *</Label>
                <select
                  id="newUserRole"
                  value={newUser.role}
                  onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}
                  className="w-full p-2 border border-gray-300 rounded-md"
                >
                  <option value="employee">موظف</option>
                  <option value="lawyer">محامي</option>
                  <option value="admin">مدير</option>
                </select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="newUserPassword">كلمة المرور *</Label>
                <Input
                  id="newUserPassword"
                  type="password"
                  value={newUser.password}
                  onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                  placeholder="أدخل كلمة المرور"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="newUserConfirmPassword">تأكيد كلمة المرور *</Label>
                <Input
                  id="newUserConfirmPassword"
                  type="password"
                  value={newUser.confirmPassword}
                  onChange={(e) => setNewUser({ ...newUser, confirmPassword: e.target.value })}
                  placeholder="أعد إدخال كلمة المرور"
                />
              </div>
            </div>

            <div className="mt-6 flex justify-end gap-3">
              <Button
                variant="outline"
                onClick={() => setShowAddUserForm(false)}
              >
                إلغاء
              </Button>
              <Button onClick={handleAddUser}>
                إضافة المستخدم
              </Button>
            </div>
          </Dialog.Panel>
        </div>
      </Dialog>

      {/* Permissions Management Dialog */}
      <Dialog open={showPermissionsDialog} onClose={() => setShowPermissionsDialog(false)} className="relative z-50">
        <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
        
        <div className="fixed inset-0 flex items-center justify-center p-4 overflow-y-auto">
          <Dialog.Panel className="mx-auto max-w-4xl w-full rounded-lg bg-white p-6 my-8">
            <Dialog.Title className="text-lg font-medium text-gray-900 mb-4 flex justify-between items-center">
              <span>إدارة صلاحيات: {selectedUserForPermissions?.name}</span>
              <Badge>{selectedUserForPermissions?.role === 'admin' ? 'مدير' : selectedUserForPermissions?.role === 'lawyer' ? 'محامي' : 'موظف'}</Badge>
            </Dialog.Title>
            
            {loadingPermissions ? (
              <div className="text-center py-8">جاري تحميل الصلاحيات...</div>
            ) : (
              <div className="space-y-4">
                <div className="flex justify-between items-center mb-4">
                  <p className="text-sm text-gray-600">{selectedPermissions.length} صلاحية محددة</p>
                  <Button variant="outline" onClick={handleResetPermissions}>
                    إعادة تعيين إلى الافتراضي
                  </Button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-h-96 overflow-y-auto">
                  {Object.entries(PERMISSION_CATEGORIES).map(([categoryKey, category]) => (
                    <Card key={categoryKey} className="p-4">
                      <h3 className="font-medium mb-3 flex items-center gap-2">
                        {category.name}
                        <Badge variant="outline">{category.permissions.length}</Badge>
                      </h3>
                      <div className="space-y-2">
                        {category.permissions.map(permission => (
                          <div key={permission} className="flex items-center justify-between">
                            <label className="text-sm cursor-pointer flex items-center gap-2">
                              <input
                                type="checkbox"
                                checked={selectedPermissions.includes(permission)}
                                onChange={() => handleTogglePermission(permission)}
                                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                              />
                              {PERMISSION_LABELS[permission] || permission}
                            </label>
                            {selectedPermissions.includes(permission) && (
                              <CheckCircle className="h-4 w-4 text-green-600" />
                            )}
                          </div>
                        ))}
                      </div>
                    </Card>
                  ))}
                </div>

                <div className="mt-6 flex justify-end gap-3 border-t pt-4">
                  <Button
                    variant="outline"
                    onClick={() => setShowPermissionsDialog(false)}
                  >
                    إلغاء
                  </Button>
                  <Button onClick={handleSavePermissions} className="bg-blue-600 hover:bg-blue-700">
                    حفظ الصلاحيات
                  </Button>
                </div>
              </div>
            )}
          </Dialog.Panel>
        </div>
      </Dialog>
      </div>
    </Layout>
  );
};

export default SettingsPage;
